/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// Members + invitations of one project (redesign artboard 23). Used by
// /projects/members (global project) and /projects/:id/settings.

import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { differenceInCalendarDays, format } from 'date-fns';
import toast from 'react-hot-toast';
import { projectService } from '../../services/api';
import { ProjectMember, Invitation, InvitationStatus, ProjectRole } from '../../types';
import { useAuthStore } from '../../store/authStore';
import InviteMemberModal from '../project/InviteMemberModal';
import EditPermissionsModal from '../project/EditPermissionsModal';
import ConfirmModal from '../common/ConfirmModal';
import { Button, SegmentTabs, Skeleton, Tag, Tone } from '../console/ui';

interface ConfirmAction {
  type: 'remove' | 'revoke' | 'deactivate' | 'reactivate' | 'resend';
  userId?: string;
  invitationId?: string;
  email: string;
}

interface ProjectTeamProps {
  projectId: string;
  ownerId?: string;
  /** Called with the active member count so a parent can show it in a tab badge. */
  onMemberCount?: (n: number) => void;
}

const INVITATION_TONE: Record<InvitationStatus, Tone> = {
  pending: 'warn',
  accepted: 'ok',
  expired: 'off',
  revoked: 'err',
};

const ROLE_HELP: Array<{ name: string; body: string }> = [
  { name: 'Owner', body: 'Everything, including deleting the project. One per project.' },
  { name: 'Admin', body: 'Create, read, update and delete every resource in the project.' },
  { name: 'Member', body: 'Read access by default. Create, update and delete can be granted per member.' },
];

const Avatar: React.FC<{ label: string; muted?: boolean }> = ({ label, muted }) => (
  <span
    className={`w-8 h-8 rounded-full grid place-items-center font-semibold text-[13px] flex-none ${
      muted ? 'bg-ps-raised text-ps-subtle' : 'bg-ps-accent-soft text-ps-accent'
    }`}
    aria-hidden="true"
  >
    {(label || 'U')[0].toUpperCase()}
  </span>
);

const roleTag = (role: ProjectRole, isOwner: boolean) =>
  isOwner ? <Tag tone="accent">Owner</Tag> : role === 'project_admin' ? <Tag tone="info">Admin</Tag> : <Tag tone="off">Member</Tag>;

// "Today" / "Yesterday" / "5 days ago" / "3 Mar 2026"; em dash if never active.
const formatLastActive = (iso?: string | null) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  const days = differenceInCalendarDays(new Date(), d);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  return format(d, 'd MMM yyyy');
};

const ProjectTeam: React.FC<ProjectTeamProps> = ({ projectId, ownerId, onMemberCount }) => {
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const [tab, setTab] = useState<'members' | 'invitations'>('members');
  const [emails, setEmails] = useState('');
  const [role, setRole] = useState<ProjectRole>('project_user');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingMember, setEditingMember] = useState<ProjectMember | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [invitationSearch, setInvitationSearch] = useState('');
  const [invitationStatusFilter, setInvitationStatusFilter] = useState<InvitationStatus | 'all'>('pending');

  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['project-members', projectId],
    queryFn: () => projectService.getMembers(projectId).then((res) => res.data),
    enabled: !!projectId,
  });

  const { data: invitationsData, isLoading: invitationsLoading } = useQuery({
    queryKey: ['project-invitations', projectId],
    queryFn: () => projectService.getInvitations(projectId).then((res) => res.data),
    enabled: !!projectId,
  });

  const members: ProjectMember[] = useMemo(() => (Array.isArray(membersData) ? membersData : []), [membersData]);
  // Older backends don't send last_active_at at all; hide the column then.
  const showLastActive = members.some((m) => 'last_active_at' in m);
  const memberCols = showLastActive ? 6 : 5;
  const invitations: Invitation[] = useMemo(() => (Array.isArray(invitationsData) ? invitationsData : []), [invitationsData]);
  const pendingCount = invitations.filter((i) => i.status === 'pending').length;

  React.useEffect(() => {
    onMemberCount?.(members.length);
  }, [members.length, onMemberCount]);

  const errMsg = (error: any, fallback: string) => error?.response?.data?.error || fallback;

  // Inline invite: one request per address (the API takes a single email).
  const inviteMutation = useMutation({
    mutationFn: async (list: string[]) => {
      const results = await Promise.allSettled(list.map((email) => projectService.inviteMember(projectId, { email, role })));
      return results.map((r, i) => ({ email: list[i], ok: r.status === 'fulfilled', error: r.status === 'rejected' ? r.reason : null }));
    },
    onSuccess: (results) => {
      queryClient.invalidateQueries({ queryKey: ['project-invitations', projectId] });
      const sent = results.filter((r) => r.ok);
      const failed = results.filter((r) => !r.ok);
      if (sent.length) toast.success(sent.length === 1 ? `Invitation sent to ${sent[0].email}` : `${sent.length} invitations sent`);
      failed.forEach((f) => toast.error(`${f.email}: ${errMsg(f.error, 'failed to send invitation')}`));
      setEmails(failed.map((f) => f.email).join(', '));
      if (sent.length) setTab('invitations');
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => projectService.removeMember(projectId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-members', projectId] });
      toast.success('Member removed successfully');
      setConfirmAction(null);
    },
    onError: (error: any) => toast.error(errMsg(error, 'Failed to remove member')),
  });

  const revokeInvitationMutation = useMutation({
    mutationFn: (invitationId: string) => projectService.revokeInvitation(projectId, invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-invitations', projectId] });
      toast.success('Invitation revoked');
      setConfirmAction(null);
    },
    onError: (error: any) => toast.error(errMsg(error, 'Failed to revoke invitation')),
  });

  const toggleMemberStatusMutation = useMutation({
    mutationFn: ({ userId, isActive }: { userId: string; isActive: boolean }) =>
      projectService.toggleMemberStatus(projectId, userId, isActive),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project-members', projectId] });
      toast.success(variables.isActive ? 'Member reactivated' : 'Member deactivated');
      setConfirmAction(null);
    },
    onError: (error: any) => toast.error(errMsg(error, 'Failed to update member status')),
  });

  const resendInvitationMutation = useMutation({
    mutationFn: (invitationId: string) => projectService.resendInvitation(projectId, invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-invitations', projectId] });
      toast.success('Invitation resent successfully');
      setConfirmAction(null);
    },
    onError: (error: any) => toast.error(errMsg(error, 'Failed to resend invitation')),
  });

  const filteredInvitations = useMemo(
    () =>
      invitations.filter((inv) => {
        if (invitationStatusFilter !== 'all' && inv.status !== invitationStatusFilter) return false;
        const q = invitationSearch.trim().toLowerCase();
        return !q || inv.email.toLowerCase().includes(q);
      }),
    [invitations, invitationStatusFilter, invitationSearch],
  );

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    const list = Array.from(
      new Set(
        emails
          .split(/[\s,;]+/)
          .map((s) => s.trim())
          .filter(Boolean),
      ),
    );
    if (list.length === 0) {
      toast.error('Enter at least one email address');
      return;
    }
    const invalid = list.filter((s) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s));
    if (invalid.length) {
      toast.error(`Not a valid email: ${invalid.join(', ')}`);
      return;
    }
    inviteMutation.mutate(list);
  };

  const handleConfirm = () => {
    if (!confirmAction) return;
    const { type, userId, invitationId } = confirmAction;
    if (type === 'remove' && userId) removeMemberMutation.mutate(userId);
    if (type === 'revoke' && invitationId) revokeInvitationMutation.mutate(invitationId);
    if (type === 'deactivate' && userId) toggleMemberStatusMutation.mutate({ userId, isActive: false });
    if (type === 'reactivate' && userId) toggleMemberStatusMutation.mutate({ userId, isActive: true });
    if (type === 'resend' && invitationId) resendInvitationMutation.mutate(invitationId);
  };

  const confirmProps = (() => {
    if (!confirmAction) return null;
    const { email } = confirmAction;
    switch (confirmAction.type) {
      case 'remove':
        return {
          title: 'Remove member',
          message: `Remove ${email} from this project? They will lose all access to project resources.`,
          confirmText: 'Remove member',
          variant: 'danger' as const,
        };
      case 'revoke':
        return {
          title: 'Revoke invitation',
          message: `Revoke the invitation for ${email}? They will no longer be able to join this project.`,
          confirmText: 'Revoke invitation',
          variant: 'warning' as const,
        };
      case 'deactivate':
        return {
          title: 'Deactivate member',
          message: `Deactivate ${email}? They will temporarily lose access to this project but can be reactivated later.`,
          confirmText: 'Deactivate',
          variant: 'warning' as const,
        };
      case 'reactivate':
        return {
          title: 'Reactivate member',
          message: `Reactivate ${email}? They will regain access to this project.`,
          confirmText: 'Reactivate',
          variant: 'info' as const,
        };
      case 'resend':
        return {
          title: 'Resend invitation',
          message: `Resend the invitation to ${email}? A new invitation email will be sent with a fresh token.`,
          confirmText: 'Resend',
          variant: 'info' as const,
        };
    }
  })();

  const busy =
    removeMemberMutation.isPending ||
    revokeInvitationMutation.isPending ||
    toggleMemberStatusMutation.isPending ||
    resendInvitationMutation.isPending;

  return (
    <>
      <section className="ps-card p-5 flex flex-col gap-3" aria-labelledby="invite-heading">
        <div className="flex flex-wrap justify-between items-baseline gap-2">
          <h2 id="invite-heading" className="m-0 text-base font-medium text-ps-text">
            Invite people
          </h2>
          <button type="button" className="ps-link text-sm" onClick={() => setShowInviteModal(true)}>
            Invite with custom permissions
          </button>
        </div>
        <form onSubmit={handleInvite} className="flex flex-wrap gap-2.5">
          <label className="sr-only" htmlFor="invite-emails">
            Email addresses
          </label>
          <input
            id="invite-emails"
            className="ps-input flex-[3_1_320px] w-auto"
            placeholder="name@company.com, another@company.com"
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            autoComplete="off"
          />
          <label className="sr-only" htmlFor="invite-role">
            Role
          </label>
          <select
            id="invite-role"
            className="ps-input flex-[1_1_140px] w-auto"
            value={role}
            onChange={(e) => setRole(e.target.value as ProjectRole)}
          >
            <option value="project_user">Member</option>
            <option value="project_admin">Admin</option>
          </select>
          <Button type="submit" disabled={inviteMutation.isPending}>
            {inviteMutation.isPending ? 'Sending…' : 'Send invites'}
          </Button>
        </form>
      </section>

      <SegmentTabs
        label="Team"
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'members', label: 'Members', badge: members.length || undefined },
          { value: 'invitations', label: 'Invitations', badge: pendingCount ? `${pendingCount} pending` : undefined },
        ]}
      />

      {tab === 'members' && (
        <div className="ps-card overflow-x-auto">
          <table className="ps-tbl">
            <thead>
              <tr>
                <th>Member</th>
                <th>Role</th>
                <th>Permissions</th>
                <th>Joined</th>
                {showLastActive && <th>Last active</th>}
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {membersLoading ? (
                [0, 1].map((i) => (
                  <tr key={i}>
                    <td colSpan={memberCols}>
                      <Skeleton className="h-8" />
                    </td>
                  </tr>
                ))
              ) : members.length === 0 ? (
                <tr>
                  <td colSpan={memberCols} className="!py-10 text-center text-ps-muted !whitespace-normal">
                    No members yet. Invite people above to collaborate on this project.
                  </td>
                </tr>
              ) : (
                members.map((member) => {
                  const isYou = member.user_id === currentUserId;
                  const isOwner = !!ownerId && member.user_id === ownerId;
                  const inactive = member.is_active === false;
                  const email = member.user?.email || 'this member';
                  const perms = [
                    member.can_create && 'Create',
                    member.can_read && 'Read',
                    member.can_update && 'Update',
                    member.can_delete && 'Delete',
                  ].filter(Boolean) as string[];
                  return (
                    <tr key={member.id} className={inactive ? 'opacity-60' : undefined}>
                      <td>
                        <div className="flex gap-2.5 items-center">
                          <Avatar label={member.user?.name || member.user?.email || 'U'} muted={inactive} />
                          <div className="min-w-0">
                            <div className="font-medium text-ps-text">
                              {member.user?.name || member.user?.email || 'Unknown user'}
                              {isYou && <span className="text-ps-subtle font-normal"> (you)</span>}
                              {inactive && (
                                <Tag tone="err" className="ml-2">
                                  Deactivated
                                </Tag>
                              )}
                            </div>
                            {member.user?.name && <div className="text-ps-subtle text-xs">{member.user?.email}</div>}
                          </div>
                        </div>
                      </td>
                      <td>{roleTag(member.role, isOwner)}</td>
                      <td className="text-ps-muted text-xs">{isOwner ? 'All' : perms.join(' · ') || 'None'}</td>
                      <td className="text-ps-muted">{member.created_at ? format(new Date(member.created_at), 'd MMM yyyy') : '-'}</td>
                      {showLastActive &&
                        (() => {
                          const label = formatLastActive(member.last_active_at);
                          const known = label !== '-';
                          return (
                            <td
                              className={known ? 'text-ps-text' : 'text-ps-muted'}
                              title={known ? format(new Date(member.last_active_at as string), 'd MMM yyyy, HH:mm') : 'No activity recorded'}
                            >
                              {label}
                            </td>
                          );
                        })()}
                      <td className="text-right">
                        {!isOwner && (
                          <div className="flex justify-end gap-1.5">
                            <Button variant="secondary" size="sm" onClick={() => setEditingMember(member)}>
                              Permissions
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() =>
                                setConfirmAction({ type: inactive ? 'reactivate' : 'deactivate', userId: member.user_id, email })
                              }
                            >
                              {inactive ? 'Reactivate' : 'Deactivate'}
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => setConfirmAction({ type: 'remove', userId: member.user_id, email })}
                            >
                              Remove
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'invitations' && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-2.5">
            <label className="sr-only" htmlFor="inv-search">
              Search invitations
            </label>
            <input
              id="inv-search"
              className="ps-input flex-[1_1_220px] w-auto"
              placeholder="Search by email"
              value={invitationSearch}
              onChange={(e) => setInvitationSearch(e.target.value)}
            />
            <label className="sr-only" htmlFor="inv-status">
              Status
            </label>
            <select
              id="inv-status"
              className="ps-input w-auto"
              value={invitationStatusFilter}
              onChange={(e) => setInvitationStatusFilter(e.target.value as InvitationStatus | 'all')}
            >
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="expired">Expired</option>
              <option value="revoked">Revoked</option>
              <option value="all">All statuses</option>
            </select>
          </div>
          <div className="ps-card overflow-x-auto">
            <table className="ps-tbl">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Invited</th>
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {invitationsLoading ? (
                  <tr>
                    <td colSpan={5}>
                      <Skeleton className="h-8" />
                    </td>
                  </tr>
                ) : filteredInvitations.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="!py-10 text-center text-ps-muted !whitespace-normal">
                      {invitations.length === 0 ? (
                        'No invitations yet.'
                      ) : (
                        <>
                          No matching invitations.{' '}
                          <button
                            type="button"
                            className="ps-link"
                            onClick={() => {
                              setInvitationSearch('');
                              setInvitationStatusFilter('all');
                            }}
                          >
                            Clear filters
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredInvitations.map((inv) => (
                    <tr key={inv.id}>
                      <td className="text-ps-text">{inv.email}</td>
                      <td>{roleTag(inv.role, false)}</td>
                      <td>
                        <Tag tone={INVITATION_TONE[inv.status] || 'off'}>
                          {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                        </Tag>
                      </td>
                      <td className="text-ps-muted">
                        {format(new Date(inv.created_at), 'd MMM yyyy')}
                        {inv.status === 'pending' && inv.expires_at && (
                          <span className="text-ps-warn"> · expires {format(new Date(inv.expires_at), 'd MMM')}</span>
                        )}
                      </td>
                      <td className="text-right">
                        {(inv.status === 'pending' || inv.status === 'expired') && (
                          <div className="flex justify-end gap-1.5">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setConfirmAction({ type: 'resend', invitationId: inv.id, email: inv.email })}
                            >
                              Resend
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => setConfirmAction({ type: 'revoke', invitationId: inv.id, email: inv.email })}
                            >
                              Revoke
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <section className="flex flex-col gap-3" aria-labelledby="roles-heading">
        <h2 id="roles-heading" className="m-0 text-base font-medium text-ps-text">
          What each role can do
        </h2>
        <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(100%,220px),1fr))]">
          {ROLE_HELP.map((r) => (
            <div key={r.name} className="ps-card p-4 flex flex-col gap-1">
              <b className="text-ps-text font-semibold">{r.name}</b>
              <span className="text-ps-muted text-[13px]">{r.body}</span>
            </div>
          ))}
        </div>
      </section>

      <InviteMemberModal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} projectId={projectId} />

      {editingMember && (
        <EditPermissionsModal
          isOpen={!!editingMember}
          onClose={() => setEditingMember(null)}
          projectId={projectId}
          member={editingMember}
        />
      )}

      {confirmAction && confirmProps && (
        <ConfirmModal
          isOpen={!!confirmAction}
          onClose={() => setConfirmAction(null)}
          onConfirm={handleConfirm}
          isLoading={busy}
          {...confirmProps}
        />
      )}
    </>
  );
};

export default ProjectTeam;
